/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * 录音相关逻辑 hook
 * 管理语音输入、浏览器语音识别、MediaRecorder 录音、倒计时等功能
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Modal } from 'tea-component';
import { useInterval, useUnmount } from 'ahooks';
import { speechToText } from '../../../service/service';
import { buriedBtnPoint } from '@home/utils/buriedPoint';

// 获取浏览器 SpeechRecognition 接口
// @ts-ignore - SpeechRecognition 在标准 TS lib 中缺失，仅运行时存在
const SpeechRecognitionAPI =
  // @ts-ignore
  window.SpeechRecognition || window.webkitSpeechRecognition;
// @ts-ignore - SpeechRecognition 类型同样缺失
let recognition: SpeechRecognition | null = null;

// 如果浏览器支持 SpeechRecognition
if (SpeechRecognitionAPI) {
  recognition = new SpeechRecognitionAPI();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'zh-CN'; // 默认使用中文识别
}

const DEFAULT_COUNTDOWN_TIME = 30;

interface IUseVoiceRecordingParams {
  /** 会话 ID，用于埋点 */
  sessionId?: string;
  /** 来源标识，用于埋点 */
  from?: string;
  /** 消息 ID，用于埋点 */
  messageId?: string;
  /** 用户信息，含 openid，用于语音转文字 API */
  userInfo?: { openid?: string; [key: string]: any; };
  /** 将识别结果写入输入框的回调 */
  setInputValue: (value: string | ((prev: string) => string)) => void;
}

const useVoiceRecording = ({
  sessionId,
  from,
  messageId,
  userInfo,
  setInputValue,
}: IUseVoiceRecordingParams) => {
  const [isRecording, setIsRecording] = useState(false);
  const [useBrowserRecognition, setUseBrowserRecognition] = useState(false);
  const [countdown, setCountdown] = useState(DEFAULT_COUNTDOWN_TIME);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownRunning, setCountdownRunning] = useState(false);
  const [speekLoading, setSpeekLoading] = useState<boolean>(false);
  const [isNoRecognize, setIsNoRecognize] = useState(false);
  const [hasMicPermission, setHasMicPermission] = useState<boolean | null>(null);

  const isCancelledRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const noRecognizeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 用 useInterval 管理倒计时
  useInterval(
    () => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    },
    countdownRunning ? 1000 : undefined,
  );

  // 启动倒计时
  const startCountdown = useCallback(() => {
    setCountdown(DEFAULT_COUNTDOWN_TIME);
    setShowCountdown(true);
    setCountdownRunning(true);
  }, []);

  // 停止倒计时
  const stopCountdown = useCallback(() => {
    setCountdownRunning(false);
    setShowCountdown(false);
  }, []);

  // 处理音频转文字
  const processAudioToText = useCallback(async (audioBlob: Blob) => {
    setSpeekLoading(true);
    setIsNoRecognize(false);

    try {
      const data: any = await speechToText(audioBlob, userInfo?.openid || '');
      setSpeekLoading(false);
      if (data?.result?.voiceTextStr) {
        setInputValue?.(data.result.voiceTextStr);
      } else {
        setIsNoRecognize(true);
        noRecognizeTimerRef.current = setTimeout(() => {
          setIsNoRecognize(false);
        }, 3000);
      }
    } catch (error) {
      setSpeekLoading(false);
      setIsNoRecognize(true);
      noRecognizeTimerRef.current = setTimeout(() => {
        setIsNoRecognize(false);
      }, 3000);
    }
  }, [userInfo?.openid, setInputValue]);

  // 停止录音
  const stopRecording = useCallback(() => {
    stopCountdown();

    if (useBrowserRecognition && recognition) {
      recognition.stop();
      setIsRecording(false);
      return;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
  }, [useBrowserRecognition, stopCountdown]);

  // 麦克风权限检查
  const checkMicPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // 开始浏览器语音识别
  const startBrowserRecognition = useCallback(() => {
    if (!recognition) {
      Modal.alert({
        message: '您的浏览器不支持语音识别功能',
      });
      return;
    }

    try {
      isCancelledRef.current = false;
      recognition.start();
      setIsRecording(true);
      startCountdown();
    } catch (error) {
      console.error('浏览器语音识别启动失败:', error);
    }
  }, [startCountdown]);

  // 开始录音
  const startRecording = useCallback(async () => {
    try {
      isCancelledRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav',
        });
        audioChunksRef.current = [];

        if (!isCancelledRef.current) {
          await processAudioToText(audioBlob);
        }

        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        stopCountdown();
        isCancelledRef.current = false;
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      startCountdown();
    } catch (error) {
      console.error('录音失败:', error);
      setUseBrowserRecognition(true);
      startBrowserRecognition();
    }
  }, [processAudioToText, stopCountdown, startCountdown, startBrowserRecognition]);

  // 处理语音输入
  const handleVoiceInput = useCallback(async () => {
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '700501007',
      extInfo: JSON.stringify({
        sessionId,
        from,
        messageId,
      }),
      typeId: 'aigc',
    });

    if (isRecording) {
      stopRecording();
      return;
    }

    if (hasMicPermission === null || hasMicPermission === false) {
      const permitted = await checkMicPermission();
      setHasMicPermission(permitted);

      if (!permitted) {
        return;
      }
    }

    if (useBrowserRecognition || !window.MediaRecorder) {
      startBrowserRecognition();
    } else {
      startRecording();
    }
  }, [
    sessionId, from, messageId, isRecording, hasMicPermission,
    useBrowserRecognition, stopRecording, checkMicPermission,
    startBrowserRecognition, startRecording,
  ]);

  // 确认录音
  const confirmRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // 取消录音
  const cancelRecording = useCallback(() => {
    if (speekLoading) return;
    isCancelledRef.current = true;
    stopCountdown();

    if (useBrowserRecognition && recognition) {
      recognition.stop();
      setIsRecording(false);
      return;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
  }, [speekLoading, useBrowserRecognition, stopCountdown]);

  // 配置浏览器 SpeechRecognition
  useEffect(() => {
    if (!recognition) return;

    recognition.onresult = (event: any) => {
      if (isCancelledRef.current) return;

      const { results } = event;
      let transcript = '';

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0;i < results.length;i++) {
        const result = results[i];
        if (result?.[0] && typeof result[0].transcript === 'string') {
          transcript += result[0].transcript;
        }
      }

      if (results[0]?.isFinal) {
        setInputValue(transcript);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('语音识别错误:', event.error, event.message);
      setIsRecording(false);
      stopCountdown();
      isCancelledRef.current = false;
    };

    recognition.onend = () => {
      setIsRecording(false);
      stopCountdown();
      isCancelledRef.current = false;
    };

    return () => {
      if (recognition && isRecording) {
        recognition.stop();
      }
    };
  }, [isCancelledRef.current]);

  useUnmount(() => {
    if (noRecognizeTimerRef.current) clearTimeout(noRecognizeTimerRef.current);
  });

  // 倒计时显示文本
  const voiceCountdownText = useMemo(() => {
    if (countdown === 0) {
      return '30';
    }
    const left = 30 - countdown;
    if (left < 10) {
      return `0${left}`;
    }
    return `${left}`;
  }, [countdown]);

  return {
    isRecording,
    speekLoading,
    isNoRecognize,
    countdown,
    showCountdown,
    voiceCountdownText,
    handleVoiceInput,
    confirmRecording,
    cancelRecording,
    stopRecording,
  };
};

export default useVoiceRecording;
